import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { recordPayment } from '../../utils/paymentUtils'; // The utility function we created
import { generateInvoiceNumber } from '../../utils/generateInvoiceNumber'

const PosIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
    <rect x="4" y="3" width="16" height="18" rx="2" fill="#2563eb"/>
    <rect x="7" y="6" width="10" height="2" rx="1" fill="#fff"/>
    <rect x="7" y="10" width="10" height="2" rx="1" fill="#fff"/>
    <rect x="7" y="14" width="4" height="2" rx="1" fill="#fff"/>
    <rect x="13" y="14" width="4" height="2" rx="1" fill="#fff"/>
  </svg>
);

const PaymentForm = () => {
  const { id } = useParams(); 
  const [pkg, setPkg] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [paidBy, setPaidBy] = useState('');
  const [reference, setReference] = useState('');
  //const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [user] = useAuthState(auth);
  const [userProfile, setUserProfile] = useState(null);
  const [generatedInvoiceNumber, setGeneratedInvoiceNumber] = useState(generateInvoiceNumber());
  const [userRole, setUserRole] = useState(""); // Add this line
  const navigate = useNavigate();
 

  const paymentMethods = [
    { value: 'linx', label: 'Linx', icon: <PosIcon /> },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
    { value: 'cash', label: 'Cash', icon: '💵' },
    { value: 'credit_card', label: 'Credit Card', icon: '💳' }
  ];

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }
      
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile(data);
          setUserRole(Array.isArray(data.roles) ? data.roles[0] : ""); // Get the first role or adjust as needed
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    
    fetchUserProfile();
  }, [user, navigate]);

  // Fetch package and autofill amount for all users (not just drivers)
  useEffect(() => {
    const fetchPackage = async () => {
      setLoading(true); 
      if (!user) return;
      
      try {
        // Fetch package info
        const docRef = doc(db, 'packages', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const packageData = { id: docSnap.id, ...docSnap.data() };
          setPkg(packageData);
          // Autofill amount for all users and make uneditable
          if (packageData.total !== undefined && packageData.total !== null) {
            setAmount(packageData.total.toString());
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackage();
  }, [user, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || !paymentMethod) {
      alert('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      await recordPayment(
        id,
        parseFloat(amount),
        paymentMethod,
        paidBy,
        generatedInvoiceNumber,
        //notes
      );
      
      alert('Payment recorded successfully!');
      navigate('/payments'); // Navigate to payment history
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePackageChange = (packageId) => {
    setSelectedPackage(packageId);
    
    // Auto-fill amount if package has totalAmount
    const selectedPkg = pkg.find(pkg => pkg.id === packageId);
    if (selectedPkg && selectedPkg.totalAmount) {
      setAmount(selectedPkg.totalAmount.toString());
    }
  };

  const handleGenerateNewInvoiceNumber = () => {
    const newInvoiceNumber = generateInvoiceNumber();
    setGeneratedInvoiceNumber(newInvoiceNumber);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Record Payment</h1>
        
        <div className="bg-white shadow rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Package Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package <span className="text-red-500">*</span>
              </label>
              <p className="block text-sm font-medium text-gray-700 mb-2">{pkg.trackingNumber}</p>
              <p className="block text-sm font-medium text-gray-700 mb-2">{pkg.details}</p>
              <p className="block text-sm font-medium text-gray-700 mb-2">{pkg.recipient}</p>
              <p className="block text-sm font-medium text-gray-700 mb-2">{pkg.total}</p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                  disabled // <-- Always uneditable
                  readOnly // <-- Always uneditable
                />
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {paymentMethods.map(method => (
                  <label
                    key={method.value}
                    className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors
                      ${paymentMethod === method.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.value}
                      checked={paymentMethod === method.value}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-xl mr-2">{method.icon}</span>
                    <span className="text-sm font-medium">{method.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Paid By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paid By: {user.email} <span className="text-red-500">*</span>
              </label>
              {/* <select
                value={paidBy}
                onChange={(e) => setPaidBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select who made the payment...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </select> */}
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference/Invoice Number
              </label>
              {generatedInvoiceNumber}

              {/* <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="INV-2024-001"
              /> */}
            </div>

            {/* Notes */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes about this payment..."
              />
            </div> */}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/packages')}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;